import {
  useGetSlotByIdListMutation,
  useLazyGetSlotByListNameQuery,
  useLazyGetSlotFiltersByCategoryQuery,
} from 'features/api/apiSlice';
import { useAppDispatch, useTypedSelector } from 'lib/centralStore';
import { CMS_SLOT_MODEL_API_KEY, CONSOLE_GIOCHI_SLOT_MODEL_API_KEY } from 'lib/datoCms/constants';
import { DatoCmsConsoleGiochiItemList, DatoCmsItemList } from 'lib/datoCms/types';
import { useCallback, useEffect, useMemo } from 'react';
import {
  GamesGameFilterTypeDto,
  GamesSkillGameDto,
  GamesUIComponentFilterType,
  ItemWithTranslation,
  SportsFilterTemplateTopLive,
} from 'types/swagger';
import {
  incrementPageNumber,
  removeFilter,
  resetFilters,
  setCategoryValue,
  setFunctionalityValue,
  setLinesValue,
  setPageNumber,
  setProducerValue,
  setSelectedFiltersLenght,
  setVolatilityValue,
  unsetCategoryValue,
  unsetFunctionalityValue,
  unsetLinesValue,
  unsetProducerValue,
  unsetVolatilityValue,
} from './slotArchiveSlice';
import { SlotFiltersTypes, calculateAllSelectedFiltersTogether } from './utils/utils';

import { CardList } from 'components/cardList';
import { selectIsLoading } from 'features/location/selectors';
import { useGetItemListFromModularContent } from 'hooks/useGetItemListFromModularContent';
import { useGetLabelsByKeys } from 'hooks/useLingUI';
import { useRouter } from 'next/router';
import React from 'react';
import { pageSize } from 'utility/constant';
import { SlotArchiveFilters } from './components/slotArchiveFilters';
import { useAllItems } from './slotArchiveHooks';

export type SlotArchiveProps = {
  children?: React.ReactNode | undefined;
  slug?: string;
  games?: Array<GamesSkillGameDto>;
  slotListModularContent?: Array<DatoCmsItemList | DatoCmsConsoleGiochiItemList>;
};

// TODO: remove ItemWithTranslation once Dto updated
export type SlotFilter =
  | GamesGameFilterTypeDto
  | (SportsFilterTemplateTopLive &
      ItemWithTranslation<{ label: string; values: string[] }> & {
        typeOfItem: GamesUIComponentFilterType;
        tooltipContent?: string | undefined;
      });

export const SlotArchive = ({ children, games, slotListModularContent }: SlotArchiveProps) => {
  const dispatch = useAppDispatch();
  const [labelReset, labelFilters, labelShowResults, apiGenericErrorLabel] = useGetLabelsByKeys([
    'reset',
    'filtri-modale-giochi',
    'mostra-risultati',
    'label-api-generic-error',
  ]);
  const { selectedFilters, pageNumber, selectedFiltersLength } = useTypedSelector(({ archiveSlot }) => archiveSlot);

  const { asPath } = useRouter();

  const isPageLoading = useTypedSelector(selectIsLoading);
  const allSelectedFilters = useMemo(() => {
    return calculateAllSelectedFiltersTogether(selectedFilters);
  }, [selectedFilters]);

  const [updateSlot] = useGetSlotByIdListMutation();
  const [trigger, { data, isLoading, isFetching, isError }] = useLazyGetSlotByListNameQuery();
  const [triggerFilters, { currentData: filtersList }] = useLazyGetSlotFiltersByCategoryQuery();

  const { listName, cmsGameList, middlewareIdList } = useGetItemListFromModularContent({
    cmsModelApiKey: CMS_SLOT_MODEL_API_KEY,
    itemModularContent: slotListModularContent,
    consoleGiochiModelApiKey: CONSOLE_GIOCHI_SLOT_MODEL_API_KEY,
  });

  const handleClickResetFilters = useCallback(() => {
    dispatch(resetFilters());
    dispatch(setPageNumber(1));
  }, [dispatch]);

  const handleFunctionalityChange = (el: HTMLInputElement): void => {
    if (el.checked) {
      dispatch(setFunctionalityValue(el.value));
    } else {
      dispatch(unsetFunctionalityValue(el.value));
    }
  };
  const handleVolatilityChange = (el: HTMLInputElement): void => {
    if (el.checked) {
      dispatch(setVolatilityValue(el.value));
    } else {
      dispatch(unsetVolatilityValue(el.value));
    }
  };
  const handleLinesChange = (el: HTMLInputElement): void => {
    if (el.checked) {
      dispatch(setLinesValue(el.value));
    } else {
      dispatch(unsetLinesValue(el.value));
    }
  };
  const handleProducerChange = (el: HTMLInputElement): void => {
    if (el.checked) {
      dispatch(setProducerValue(el.value));
    } else {
      dispatch(unsetProducerValue(el.value));
    }
  };
  const handleCategoryChange = (el: HTMLInputElement): void => {
    if (el.checked) {
      dispatch(setCategoryValue(el.value));
    } else {
      dispatch(unsetCategoryValue(el.value));
    }
  };
  const handleChangeFilter = (filterType: string, el: HTMLInputElement): void => {
    const type = filterType.toLowerCase();
    switch (type) {
      case SlotFiltersTypes.FUNCTIONALITY:
        return handleFunctionalityChange(el);
      case SlotFiltersTypes.VOLATILITY:
        return handleVolatilityChange(el);
      case SlotFiltersTypes.LINES:
        return handleLinesChange(el);
      case SlotFiltersTypes.PRODUCER:
        return handleProducerChange(el);
      case SlotFiltersTypes.CATEGORY:
        return handleCategoryChange(el);
    }
  };
  const handleClickViewFilterResults = () => {
    dispatch(setPageNumber(1));
    dispatch(setSelectedFiltersLenght(allSelectedFilters.length));
  };

  const handleClickLoadMore = () => {
    dispatch(incrementPageNumber());
  };

  const handleClickRemoveSingleFilter = (value: string) => {
    dispatch(removeFilter(value));
  };

  useEffect(() => {
    dispatch(resetFilters());
    dispatch(setPageNumber(1));
  }, [asPath, dispatch]);

  useEffect(() => {
    if (isPageLoading) return;
    if (games) return;
    if (listName) {
      trigger({
        pageSize,
        listName,
        pageNumber,
        selectedFilters,
      });
      triggerFilters({
        listName,
      });
    } else if (cmsGameList) {
      updateSlot(middlewareIdList!);
    }
  }, [
    cmsGameList,
    games,
    listName,
    middlewareIdList,
    pageNumber,
    selectedFilters,
    isPageLoading,
    dispatch,
    trigger,
    triggerFilters,
    updateSlot,
  ]);

  const allItems = useAllItems();

  if (isError) {
    return <p>{apiGenericErrorLabel}</p>;
  }

  return (
    <React.Fragment>
      {listName && (
        <SlotArchiveFilters
          label={labelFilters}
          modalTitle={labelFilters}
          modalResetLabel={labelReset}
          modalDismissLabel={labelShowResults}
          modalLinesFilter={filtersList?.modalLinesFilter!}
          modalProducerFilter={filtersList?.modalProducerFilter!}
          modalCategoryFilter={filtersList?.modalCategoryFilter!}
          modalVolatilityFilter={filtersList?.modalVolatilityFilter!}
          modalFunctionalityFilter={filtersList?.modalFunctionalityFilter!}
          handleFilterChange={handleChangeFilter}
          handleResetFilters={handleClickResetFilters}
          handleClickViewResults={handleClickViewFilterResults}
          handleClickRemoveSingleFilter={handleClickRemoveSingleFilter}
        />
      )}

      <CardList
        typeOfItem="slot"
        handleClickLoadMore={() => !games && handleClickLoadMore()}
        isLoading={isLoading}
        isFetching={isFetching}
        filteredItems={allItems}
        totalCount={data?.totalCount}
        totalPages={data?.totalPages}
        hasNoFilters={!listName ? true : false}
      >
        {selectedFiltersLength === 0 && children}
      </CardList>
    </React.Fragment>
  );
};
